<script setup>
/**
 * ONE STEP, FULL SCREEN — the frame around the four bodies.
 *
 * ── WHY A SCREEN AND NOT A CARD ───────────────────────────────────────────────────────
 * A card in a list has to carry its own header, its own move and remove buttons and its own
 * expand toggle, on every row, whether or not you are looking at it. That is four controls
 * per step before anything about the step itself, and it is a quarter of how the worst card
 * in this product reached 26. Here the list is the list and the step is a screen, so the
 * four move to a `⋯` that is off screen until it is asked for, and the step's own controls
 * are the only things on it.
 *
 * ── ◀ ▶, BECAUSE THE FORTIETH EDIT IS THE REAL TEST ───────────────────────────────────
 * The strongest case against a guided composer is that it is a good writer and a mediocre
 * editor. These two links are part of the answer: you walk the steps without going back to
 * the list, and every one of them is a URL, so Back works and a step can be bookmarked.
 *
 * ── NOTHING HERE TELEPORTS ────────────────────────────────────────────────────────────
 * The `⋯` menu is a disclosure that opens in place and the removal is confirmed where the
 * button was, exactly as the task page's removal already is. `DropdownContainer`, `Popover`
 * and `Dialog` all reach `TeleportWithDirection` → `useMapGetter('accounts/isRTL')`, which
 * throws inside a render effect AFTER a test has resolved — it cannot fail an assertion,
 * only the exit code, and it killed the image build on both environments.
 */
import { computed, ref } from 'vue';

import Button from 'dashboard/components-next/button/Button.vue';

import ActionStep from './ActionStep.vue';
import HandoffStep from './HandoffStep.vue';
import MessageStep from './MessageStep.vue';
import QuestionStep from './QuestionStep.vue';
import { useWasloI18n } from '../../i18n.js';
import { toIntlLocale } from '../../helpers/intl.js';
import { problemMessage } from '../../helpers/task-flow-draft.js';

const props = defineProps({
  row: { type: Object, required: true },
  index: { type: Number, required: true },
  /** Every row, in order — for the position line and the branch pickers. */
  rows: { type: Array, default: () => [] },
  /** This step's problems, already filtered to it. */
  problems: { type: Array, default: () => [] },
  takenKeys: { type: Array, default: () => [] },
  contactFields: { type: Array, default: () => [] },
  connections: { type: Array, default: null },
  live: {
    type: Object,
    default: () => ({ labels: [], total: 0, more: false }),
  },
  /** `stepId => routeLocation`, for ◀ ▶. */
  linkFor: { type: Function, required: true },
  /** Where the list is. */
  listLink: { type: [String, Object], default: '' },
  /** The account's "Your customers write in" (P-7.1), for the bilingual boxes. */
  languages: { type: String, default: 'both' },
  /** `(index, delta) => {stepId, detail}|null`. */
  moveProblem: { type: Function, default: () => null },
});

const emit = defineEmits(['update:row', 'wiring', 'move', 'remove']);

const { t, locale } = useWasloI18n();

const key = name => `WASLO.TASKS.COMPOSE.SCREEN.${name}`;

const format = computed(
  () => new Intl.NumberFormat(toIntlLocale(locale.value))
);

const menuOpen = ref(false);
const confirmingRemoval = ref(false);
const refused = ref('');

const BODIES = {
  ask: QuestionStep,
  choose: QuestionStep,
  choose_live: QuestionStep,
  tell: MessageStep,
  call: ActionStep,
  handoff: HandoffStep,
  end: HandoffStep,
};

const type = computed(() => props.row.step?.type ?? 'ask');
const body = computed(() => BODIES[type.value] ?? QuestionStep);

/**
 * Only the props the chosen body declares.
 *
 * Vue would pass the rest through as attributes and Chatwoot's own components warn on
 * unknown ones; more to the point, a body that is handed the whole world reads as one that
 * uses it. Each of the four takes exactly what it needs.
 */
const bodyProps = computed(() => {
  if (body.value === QuestionStep) {
    return {
      row: props.row,
      rows: props.rows,
      takenKeys: props.takenKeys,
      contactFields: props.contactFields,
      connections: props.connections,
      live: props.live,
      languages: props.languages,
    };
  }
  if (body.value === ActionStep) {
    return { row: props.row, connections: props.connections };
  }
  if (body.value === MessageStep) {
    return { row: props.row, rows: props.rows, languages: props.languages };
  }
  return { row: props.row };
});

const position = computed(() =>
  t(key('POSITION'), {
    number: format.value.format(props.index + 1),
    total: format.value.format(props.rows.length),
  })
);

const previousLink = computed(() => {
  const before = props.rows[props.index - 1];
  return before ? props.linkFor(before.step?.id) : null;
});

const nextLink = computed(() => {
  const after = props.rows[props.index + 1];
  return after ? props.linkFor(after.step?.id) : null;
});

const problemLines = computed(() =>
  props.problems.map(problem => problemMessage(problem, t))
);

const titleOf = row => {
  const step = row?.step ?? {};
  if (step.type === 'ask' || step.type === 'choose') {
    return (step.questionEn ?? '').trim() || (step.name ?? '').trim();
  }
  return (step.name ?? '').trim() || (step.instruction ?? '').trim();
};

const heading = computed(() => titleOf(props.row) || t(key('UNWRITTEN')));

const moveLabel = delta => {
  const there = props.rows[props.index + delta];
  if (!there) return t(key(delta < 0 ? 'UP' : 'DOWN'));
  return t(key(delta < 0 ? 'UP_MEANS' : 'DOWN_MEANS'), {
    moved: titleOf(props.row) || props.row.step?.id,
    other: titleOf(there) || there.step?.id,
  });
};

const move = delta => {
  const problem = props.moveProblem(props.index, delta);
  if (problem) {
    refused.value = t(key('MOVE_REFUSED'), { detail: problem.detail });
    return;
  }
  refused.value = '';
  menuOpen.value = false;
  emit('move', delta);
};
</script>

<template>
  <section
    class="flex flex-col gap-6"
    :data-waslo-task-step="row.step?.id"
    :data-waslo-step-screen="row.step?.id"
    :data-type="type"
  >
    <div class="flex flex-wrap items-start justify-between gap-3">
      <div class="flex flex-col min-w-0 gap-1">
        <router-link
          v-if="listLink"
          :to="listLink"
          class="inline-flex items-center gap-2 text-sm text-n-slate-11 hover:text-n-slate-12"
          data-waslo-step-back
        >
          <span
            class="size-4 i-lucide-chevron-left rtl:rotate-180"
            aria-hidden="true"
          />
          {{ t(key('BACK')) }}
        </router-link>
        <h2 class="mb-0 text-base font-medium break-words text-n-slate-12">
          <bdi>{{ heading }}</bdi>
        </h2>
        <p class="mb-0 text-sm text-n-slate-11">{{ position }}</p>
      </div>

      <div class="flex items-center gap-1 shrink-0">
        <component
          :is="previousLink ? 'router-link' : 'span'"
          :to="previousLink || undefined"
          class="grid rounded-md place-items-center size-8"
          :class="
            previousLink
              ? 'text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-2'
              : 'text-n-slate-9'
          "
          :aria-label="t(key('PREVIOUS'))"
          :title="t(key('PREVIOUS'))"
          data-waslo-step-previous
        >
          <span
            class="size-4 i-lucide-chevron-left rtl:rotate-180"
            aria-hidden="true"
          />
        </component>
        <component
          :is="nextLink ? 'router-link' : 'span'"
          :to="nextLink || undefined"
          class="grid rounded-md place-items-center size-8"
          :class="
            nextLink
              ? 'text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-2'
              : 'text-n-slate-9'
          "
          :aria-label="t(key('NEXT'))"
          :title="t(key('NEXT'))"
          data-waslo-step-next
        >
          <span
            class="size-4 i-lucide-chevron-right rtl:rotate-180"
            aria-hidden="true"
          />
        </component>
        <button
          type="button"
          class="grid rounded-md place-items-center size-8 text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-2"
          :aria-expanded="menuOpen"
          :aria-label="t(key('MORE'))"
          :title="t(key('MORE'))"
          data-waslo-step-more
          @click="menuOpen = !menuOpen"
        >
          <span class="size-4 i-lucide-ellipsis" aria-hidden="true" />
        </button>
      </div>
    </div>

    <!-- The `⋯` menu, in place. Never a teleported overlay. -->
    <div
      v-if="menuOpen"
      class="flex flex-col gap-3 p-4 outline outline-1 rounded-xl outline-n-weak bg-n-solid-1"
      data-waslo-step-menu
    >
      <div class="flex flex-wrap items-center gap-3">
        <Button
          :label="moveLabel(-1)"
          icon="i-lucide-arrow-up"
          variant="faded"
          color="slate"
          size="sm"
          :disabled="index === 0"
          data-waslo-step-menu-up
          @click="move(-1)"
        />
        <Button
          :label="moveLabel(1)"
          icon="i-lucide-arrow-down"
          variant="faded"
          color="slate"
          size="sm"
          :disabled="index === rows.length - 1"
          data-waslo-step-menu-down
          @click="move(1)"
        />
      </div>
      <p
        v-if="refused"
        class="mb-0 text-sm text-n-amber-11"
        data-waslo-step-menu-refused
      >
        {{ refused }}
      </p>
      <div
        v-if="confirmingRemoval"
        class="flex flex-col gap-3 p-3 rounded-lg outline outline-1 outline-n-ruby-8"
        data-waslo-step-remove-confirm
      >
        <p class="mb-0 text-sm text-n-slate-12">
          {{ t(key('REMOVE_CONFIRM')) }}
        </p>
        <div class="flex flex-wrap items-center gap-3">
          <Button
            :label="t(key('REMOVE_YES'))"
            variant="solid"
            color="ruby"
            size="sm"
            data-waslo-step-remove-yes
            @click="emit('remove')"
          />
          <Button
            :label="t(key('REMOVE_NO'))"
            variant="faded"
            color="slate"
            size="sm"
            @click="confirmingRemoval = false"
          />
        </div>
      </div>
      <div v-else class="flex">
        <Button
          :label="t(key('REMOVE'))"
          icon="i-lucide-trash-2"
          variant="faded"
          color="ruby"
          size="sm"
          data-waslo-step-remove
          @click="confirmingRemoval = true"
        />
      </div>
    </div>

    <ul
      v-if="problemLines.length"
      class="flex flex-col gap-1 mb-0"
      data-waslo-step-problems
    >
      <li
        v-for="(line, at) in problemLines"
        :key="at"
        class="flex items-start gap-2 text-sm text-n-ruby-9"
        data-waslo-step-problem
      >
        <span
          class="mt-0.5 shrink-0 size-4 i-lucide-triangle-alert"
          aria-hidden="true"
        />
        <span>{{ line }}</span>
      </li>
    </ul>

    <component
      :is="body"
      v-bind="bodyProps"
      @update:row="value => emit('update:row', value)"
      @wiring="emit('wiring')"
    />

    <div class="flex">
      <router-link
        v-if="listLink"
        :to="listLink"
        class="inline-flex items-center h-9 px-4 text-sm font-medium rounded-lg bg-n-brand hover:bg-n-brand/90 text-white"
        data-waslo-step-done
      >
        {{ t(key('DONE')) }}
      </router-link>
    </div>
  </section>
</template>
